import React, { useEffect, useRef, useState } from "react";
import { Modal } from "libraries/bulma-ui-library";
import styled from "styled-components";
import { ClipLoader } from 'react-spinners'
import { toast } from "react-toastify";

/**
 * OpenInAppModel (React / JSX)
 *
 * - Shows only on likely mobile devices (touch + small screen + no hover)
 * - Tries to open the app via not HTTPS App/Universal Link but a custom link
 * - Uses a "visibilitychange" signal to cancel fallback if the app opened
 * - Falls back to Play Store / App Store when the app isn't installed
 *
 * IMPORTANT:
 * Your domain MUST be configured as:
 * - Android App Links (assetlinks.json + manifest autoVerify)
 * - iOS Universal Links (AASA + Associated Domains)
 */
const OpenInAppModel = ({
  androidPackage = "com.safelyq",
  playStoreUrl = "https://play.google.com/store/apps/details?id=com.safelyq",
  appStoreUrl = "https://apps.apple.com/us/app/chatclb/id1547997379",
  fallbackDelayMs = 2000,
}) => {

  const [showBanner, setShowBanner] = useState(false);
  const [redirectLoading, setRedirectLoading] = useState(false);
  const fallbackTimerRef = useRef(null);

  const isLikelyMobile = () => {
    if (typeof window === "undefined") return false;

    const hasTouch =
      navigator.maxTouchPoints > 0 ||
      "ontouchstart" in window;
    return hasTouch && window.innerWidth <= 900;
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isLikelyMobile()) {
      setShowBanner(true)
    };

    return () => {
      // cleanup
      if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = () => {
    setShowBanner(false);
  };

  // iOS needs JS fallback logic.
  // Android intent:already includes fallback internally.
  const clearFallback = () => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  };

  const handleOpenApp = () => {
    if (typeof window === "undefined" || typeof document === "undefined") return;
    setRedirectLoading(true);

    const ua = navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);

    // clear the fallback for ios and android
    window.addEventListener("blur", clearFallback);
    window.addEventListener("pagehide", clearFallback);

    if (isAndroid) {    
      const hostAndPath = `chatclb.dev/open/?openapp=1&ts=${Date.now()}`; // build path with timestamp
      
      // Correct intent format includes the host after intent://
      // This tries to open the app; if not installed, goes to Play Store
      // S.browser_fallback_url only triggers if the app is missing and the scheme is custom, not https.
      const intentUrlAndroid =
        `intent://${hostAndPath}#Intent;` +
        `scheme=chatclb;` +
        `package=${androidPackage};` +
        `S.browser_fallback_url=${encodeURIComponent(playStoreUrl)};` +
        `end`;
      
        // window.location.assign(intentUrl) or intent:// navigation can be too fast, so fallback isn’t triggered.
      // JS timeout fallback is more reliable as Works across more browsers and avoids the “intent fails silently” problem.
      window.location = intentUrlAndroid;
      fallbackTimerRef.current = setTimeout(() => {
        window.location = playStoreUrl;
      }, fallbackDelayMs);

      handleDismiss();
      return;
    }

    fallbackTimerRef.current = setTimeout(() => {
      if (isIOS) {
        // custom deeplinks for ios and android, not universal links as they have https://
        const deepLinkIos = `chatclb://chatclb.dev?openapp=1&ts=${Date.now()}`;
        // let deepLinkIos = `chatclb://openapp=1&ts=${Date.now()}`; // as iOS expects: chatclb://chatclb.dev/...
        
        window.location = deepLinkIos; // as ios does not use intent , so only use link
        fallbackTimerRef.current = setTimeout(() => {
          window.location.href = appStoreUrl;
        }, fallbackDelayMs);

        handleDismiss();
        return;
      }

      // if  neither android nor ios device is detected, than dismiss the banner
      toast.warning("Unsupported device");
      handleDismiss();
    }, fallbackDelayMs);
  };

  if (!showBanner) return null;

  return (
    <Modal active={showBanner}>
      <Modal.Background />
      <Modal.Card>
        <Modal.Card.Body style={{ backgroundColor: "transparent" }}>
          <Banner>
            <BannerText>Open ChatCLB in the Mobile App for a better experience!</BannerText>

            <div className="is-flex is-justify-content-center mt-2" style={{ gap: 10 }}>
              <DismissButton type="button"
                onClick={handleDismiss}>
                Use Web
              </DismissButton>

              <Button3D
                type="button"
                onClick={handleOpenApp}
              >
                {
                  redirectLoading ?
                    <div className="is-flex is-align-items-center">
                      <p className="mr-2">Loading</p>
                      <ClipLoader color="white" size={14} />
                    </div>
                    :
                    <p>Open App</p>
                }
              </Button3D>
            </div>

            <SmallHint>
              If nothing happens, you may not have the app installed, or the link isn’t verified yet.
            </SmallHint>
          </Banner>
        </Modal.Card.Body>
      </Modal.Card>
    </Modal>
  );
};

export default OpenInAppModel;

/* ------------------ styles ------------------ */

const SmallHint = styled.div`
  margin-top: 20px;
  font-size: 12px;
  opacity: 0.9;
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.35);
`;

const Banner = styled.div`
  width: 100%;
/* Horizontal gradient: light left → dark right */
  background: linear-gradient(to right, #4db8ff, #007afd);
  color: white;
  text-align: center;
  padding: 12px;

  /* 3D effect */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  border-radius: 12px;
`;

const BannerText = styled.p`
  text-align: center;
  width: 100%;
  font-weight: 600;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
`;

const Button3D = styled.button`
  padding: 4px 16px;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  margin-left: 10px;

  /* 3D button effect */
  background: linear-gradient(to bottom, #51c9eb, #30a2ff);
//   #00d9ff, #30a2ff
  box-shadow: 3px 3px 0px #2c6d91, 2px 4px 2px rgba(0,0,0,0.2);
  color: white;
  transition: all 0.1s ease-in-out;

  &:active {
    transform: translateY(4px);
    box-shadow: 0 0 0 #2c6d91, 0 2px 4px rgba(0,0,0,0.2);
  }

  &:hover {
    filter: brightness(1.1);
  }
`;

const DismissButton = styled(Button3D)`
  background: linear-gradient(to bottom, #a5a5a5, #6c6c6c);
  box-shadow: 3px 3px 0px #333, 2px 4px 2px rgba(0,0,0,0.2);

  &:active {
    transform: translateY(4px);
    box-shadow: 0 0 0 #333, 0 2px 4px rgba(0,0,0,0.2);
  }
`;
